// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useOnboardingStore } from '@/store/onboarding.store';
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type {
  IOnboardingImage,
  IUpdateOnboardingImage,
  IUpdateOnboardingOrderBody,
  IOnboardingImagesSearchQuery,
} from '@/types/onboarding.types';

/**
 * Hook Vue Query pour gérer les mutations liées aux images d'onboarding
 */
export const OnboardingMutation = () => {
  const onboardingStore = useOnboardingStore();
  const dialogStore = useDialogStore();

  // -------------------------------
  // 🔹 Création d'une image d'onboarding
  // -------------------------------
  const createOnboardingImage = useMutation<any, Error, IOnboardingImage>({
    mutationFn: (payload: IOnboardingImage) => {
      return onboardingStore.createOnboardingImage(payload);
    },
    onSuccess: async () => {
      dialogStore.visible = false;
    },
    onError: (err) => {
      handleGlobalError(err);
    },
  });

  // -------------------------------
  // 🔹 Mise à jour d'une image d'onboarding
  // -------------------------------
  const updateOnboardingImage = useMutation<any, Error, IUpdateOnboardingImage>(
    {
      mutationFn: (payload: IUpdateOnboardingImage) => {
        return onboardingStore.updateOnboardingImage(payload);
      },
      onSuccess: async () => {
        dialogStore.visible = false;
      },
      onError: (err) => {
        handleGlobalError(err);
      },
    },
  );

  // -------------------------------
  // 🔹 Mise à jour de l’ordre des images
  // -------------------------------
  const updateOnboardingOrder = useMutation<
    any,
    Error,
    IUpdateOnboardingOrderBody
  >({
    mutationFn: (body) => {
      return onboardingStore.updateOnboardingImagesOrder(body);
    },
    onError: (err) => {
      handleGlobalError(err);
    },
  });

  // -------------------------------
  // 🔹 Suppression de toutes les images d'onboarding
  // -------------------------------
  const deleteAllOnboardingImages = useMutation<any, Error, void>({
    mutationFn: () => {
      return onboardingStore.deleteAllOnboardingImages();
    },
    onSuccess: async () => {
      isLoaderLoginFormActivated.value = true;
      dialogStore.visible = false;

      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
      }, 1000);
    },
    onError: (err) => {
      handleGlobalError(err);
    },
  });

  // -------------------------------
  // 🔹 Suppression d'une image featured
  // -------------------------------
  const deleteFeaturedImageById = useMutation<any, Error, void>({
    mutationFn: () => {
      return onboardingStore.deleteFeaturedImageById();
    },
    onSuccess: async () => {
      dialogStore.visible = false;
    },
    onError: (err) => {
      handleGlobalError(err);
    },
  });

  // -------------------------------
  // 🔹 Fonctions exposées aux composants
  // -------------------------------

  const handleCreateOnboardingImage = (item: IOnboardingImage) => {
    return createOnboardingImage.mutateAsync(item);
  };

  const handleUpdateOnboardingImage = (item: any) => {
    return updateOnboardingImage.mutateAsync(item);
  };

  const handleUpdateOnboardingOrder = (body: IUpdateOnboardingOrderBody) => {
    return updateOnboardingOrder.mutateAsync(body);
  };

  const handleDeleteAllOnboardingImages = () => {
    deleteAllOnboardingImages.mutateAsync();
  };

  const handleDeleteFeaturedImage = () => {
    deleteFeaturedImageById.mutateAsync();
  };

  return {
    handleCreateOnboardingImage,
    handleUpdateOnboardingImage,
    handleUpdateOnboardingOrder,
    handleDeleteAllOnboardingImages,
    handleDeleteFeaturedImage,
  };
};

/**
 * Gestion centralisée des erreurs globales Axios
 */
const handleGlobalError = (err: unknown) => {
  const error = err as AxiosError;
  const response = error.response?.data as any;

  const message = response?.message?.[0] ?? 'Une erreur est survenue';
  isGlobalError.value = true;
  GlobalErrorMessage.value = message;

  setTimeout(() => {
    isGlobalError.value = false;
    isLoaderLoginFormActivated.value = false;
  }, 1000);
};
